import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { downloadArchive } from '../lib/downloadArchive';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  FileText,
  Eye,
  TrendingUp,
  Copy,
  Search,
  Upload,
  FileArchive,
  HardDrive,
  FolderOpen,
  Save,
  FileCode,
  ChevronRight,
  Archive,
  Download,
  ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import BundleAudit from './BundleAudit';
import XpnsrEmpty from './XpnsrEmpty';
import { XpnsrTableSkeleton } from './XpnsrSkeleton';

export default function LandingPages() {
  const { user } = useAuth();
  const { t } = useT();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const [searchParams, setSearchParams] = useSearchParams();
  const [createDialogOpen, setCreateDialogOpen] = useState(searchParams.get('new') === '1');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);

  // Strip ?new=1 after dialog opens
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: '',
    category: '',
    type: 'redirect',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: '',
    tags: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  // Entry-point picker state (shown after ZIP is uploaded with multiple pages)
  const [entryPickerOpen, setEntryPickerOpen] = useState(false);
  const [detectedPages, setDetectedPages] = useState([]);
  const [pendingUploadResult, setPendingUploadResult] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState('');

  // Funnel check dialog ({id, name} or null); opens by itself after an upload.
  const [auditItem, setAuditItem] = useState(null);

  // File editor state
  const [fileEditorOpen, setFileEditorOpen] = useState(false);
  const [fileEditorPage, setFileEditorPage] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileSaving, setFileSaving] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileDirty, setFileDirty] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/landing-pages`);
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      toast.error(t('Error loading landing pages'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.url) {
      toast.error(t('Please fill in name and URL'));
      return;
    }
    try {
      const response = await axios.post(`${API}/landing-pages`, formData);
      setPages([response.data, ...pages]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success(t('Landing page created'));
    } catch (error) {
      toast.error(t('Error creating landing page'));
    }
  };

  const handleUpdate = async () => {
    if (!editingPage) return;
    try {
      const response = await axios.put(`${API}/landing-pages/${editingPage.id}`, formData);
      setPages(pages.map(p => p.id === editingPage.id ? response.data : p));
      setEditDialogOpen(false);
      setEditingPage(null);
      resetForm();
      toast.success(t('Landing page updated'));
    } catch (error) {
      toast.error(t('Error updating landing page'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (pageId) => {
    try {
      await axios.delete(`${API}/landing-pages/${pageId}`);
      setPages(pages.filter(p => p.id !== pageId));
      setDeleteTarget(null);
      toast.success(t('Landing page archived'));
    } catch (error) {
      toast.error(t('Error deleting landing page'));
    }
  };

  const openEdit = (page) => {
    setEditingPage(page);
    setFormData({
      name: page.name,
      url: page.url,
      description: page.description || '',
      category: page.category || '',
      type: page.type || 'redirect',
      tags: page.tags || []
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', url: '', description: '', category: '', type: 'redirect', tags: [] });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('URL copied'));
  };

  // Uploaded files live on the backend origin (api host), not the frontend.
  const absUrl = (u) => (u && u.charAt(0) === '/' ? API.slice(0, API.lastIndexOf('/api')) + u : u);

  // Upload functions
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !file.name.endsWith('.zip')) {
      toast.error(t('Only ZIP files are supported'));
      return;
    }
    setUploadForm(prev => ({ ...prev, file }));
  };

  const handleUpload = async (overrideIndexFile) => {
    if (!uploadForm.name.trim()) {
      toast.error(t('Please enter a name for the landing page'));
      return;
    }
    if (!uploadForm.file) {
      toast.error(t('Please select a ZIP file'));
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('name', uploadForm.name);
    if (uploadForm.description) formData.append('description', uploadForm.description);
    if (uploadForm.category) formData.append('category', uploadForm.category);
    if (uploadForm.tags) formData.append('tags', uploadForm.tags);
    if (overrideIndexFile) formData.append('index_file', overrideIndexFile);

    try {
      setUploading(true);
      const response = await axios.post(`${API}/landing-pages/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const returnedPages = response.data.pages || [];

      // If multiple pages detected and no entry override yet, show picker
      if (returnedPages.length > 1 && !overrideIndexFile) {
        setDetectedPages(returnedPages);
        setSelectedEntry(response.data.index_file || returnedPages[0]);
        setPendingUploadResult(response.data);
        setUploadDialogOpen(false);
        setEntryPickerOpen(true);
        return;
      }

      // Add the new landing page to the list
      const newPage = {
        id: response.data.id,
        name: response.data.name,
        url: response.data.url,
        description: uploadForm.description,
        category: uploadForm.category,
        tags: uploadForm.tags ? uploadForm.tags.split(',').map(t => t.trim()) : [],
        is_uploaded: true,
        upload_path: response.data.upload_path,
        index_file: response.data.index_file,
        pages: response.data.pages || [],
        file_size: response.data.file_size,
        total_visits: 0,
        conversions: 0,
        conversion_rate: 0,
        is_active: true
      };

      setPages([newPage, ...pages]);
      setAuditItem({ id: newPage.id, name: newPage.name });
      setUploadDialogOpen(false);
      setEntryPickerOpen(false);
      setUploadForm({
        name: '',
        description: '',
        category: '',
        tags: '',
        file: null
      });
      toast.success(t('Landing page uploaded successfully'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('Error uploading landing page'));
    } finally {
      setUploading(false);
    }
  };

  const handleEntryPointConfirm = async () => {
    if (!pendingUploadResult) return;
    // Delete the auto-created record and re-upload with chosen entry
    try {
      await axios.delete(`${API}/landing-pages/${pendingUploadResult.id}`);
    } catch (_) { /* best effort */ }
    await handleUpload(selectedEntry);
    setPendingUploadResult(null);
    setDetectedPages([]);
  };

  const handleEntryPickerSkip = () => {
    // Accept auto-detected entry point — just add the pending result to the list
    if (pendingUploadResult) {
      const newPage = {
        id: pendingUploadResult.id,
        name: pendingUploadResult.name,
        url: pendingUploadResult.url,
        description: uploadForm.description,
        category: uploadForm.category,
        tags: uploadForm.tags ? uploadForm.tags.split(',').map(t => t.trim()) : [],
        is_uploaded: true,
        upload_path: pendingUploadResult.upload_path,
        index_file: pendingUploadResult.index_file,
        pages: pendingUploadResult.pages || [],
        file_size: pendingUploadResult.file_size,
        total_visits: 0,
        conversions: 0,
        conversion_rate: 0,
        is_active: true
      };
      setPages([newPage, ...pages]);
      setAuditItem({ id: newPage.id, name: newPage.name });
    }
    setEntryPickerOpen(false);
    setPendingUploadResult(null);
    setDetectedPages([]);
    setUploadForm({ name: '', description: '', category: '', tags: '', file: null });
    toast.success(t('Landing page uploaded successfully'));
  };

  // File editor functions
  const openFileEditor = async (page) => {
    setFileEditorPage(page);
    setFileEditorOpen(true);
    setActiveFile(null);
    setFileContent('');
    setFileDirty(false);
    try {
      setFileLoading(true);
      const res = await axios.get(`${API}/landing-pages/${page.id}/files`);
      setFileList(res.data.files || []);
    } catch (err) {
      toast.error(t('Error loading files'));
      setFileEditorOpen(false);
    } finally {
      setFileLoading(false);
    }
  };

  const openFile = async (filePath) => {
    if (fileDirty && !window.confirm(t('Discard unsaved changes?'))) return;
    try {
      setFileLoading(true);
      const res = await axios.get(`${API}/landing-pages/${fileEditorPage.id}/files/${filePath}`);
      setActiveFile(filePath);
      setFileContent(res.data.content);
      setFileDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error loading file'));
    } finally {
      setFileLoading(false);
    }
  };

  const editorRefreshFiles = async () => {
    if (!fileEditorPage) return;
    try {
      const res = await axios.get(API + "/landing-pages/" + fileEditorPage.id + "/files");
      setFileList(res.data.files || []);
    } catch (err) {
      toast.error(t('Error loading files'));
    }
  };

  const editorDeleteFile = async (filePath) => {
    if (!fileEditorPage || !filePath) return;
    if (!window.confirm(t('Delete this file?') + ' ' + filePath)) return;
    try {
      await axios.delete(API + "/landing-pages/" + fileEditorPage.id + "/files/" + filePath);
      if (activeFile === filePath) { setActiveFile(null); setFileContent(''); setFileDirty(false); }
      toast.success(t('File deleted'));
      await editorRefreshFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error deleting file'));
    }
  };

  const editorUploadFile = async (fileObj) => {
    if (!fileObj || !fileEditorPage) return;
    try {
      setFileLoading(true);
      const fd = new FormData();
      fd.append('file', fileObj);
      await axios.post(API + "/landing-pages/" + fileEditorPage.id + "/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t('File uploaded'));
      await editorRefreshFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error uploading file'));
    } finally {
      setFileLoading(false);
    }
  };

  const saveFile = async () => {
    if (!activeFile || !fileEditorPage) return;
    try {
      setFileSaving(true);
      await axios.put(`${API}/landing-pages/${fileEditorPage.id}/files/${activeFile}`, { content: fileContent });
      setFileDirty(false);
      toast.success(t('File saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error saving file'));
    } finally {
      setFileSaving(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      name: '',
      description: '',
      category: '',
      tags: '',
      file: null
    });
  };

  const filtered = pages.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'lead-gen', label: t('Lead generation') },
    { value: 'subscription', label: t('Subscription') },
    { value: 'download', label: t('Download') },
    { value: 'other', label: t('Other') }
  ];

  const PageFormFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input
            data-testid="landing-name-input"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('My landing')}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Category')}</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
          >
            <SelectTrigger data-testid="landing-category-select">
              <SelectValue placeholder={t('Select category')} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t('URL')}</Label>
        <Input
          data-testid="landing-url-input"
          value={formData.url}
          onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
          placeholder="https://example.com/landing"
        />
      </div>
      <div className="space-y-2">
        <Label>{t('Type')}</Label>
        <Select
          value={formData.type || 'redirect'}
          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
        >
          <SelectTrigger data-testid="landing-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="redirect">{t('Redirect (send visitor to the URL)')}</SelectItem>
            <SelectItem value="preload">{t('Preload (serve the page from the tracker domain)')}</SelectItem>
            <SelectItem value="action">{t('Action (no page — hand off to the offer)')}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t('Preload fetches the page server-side and serves it under your tracker domain; Action shows nothing and lets the stream pick an offer.')}
        </p>
      </div>
      <div className="space-y-2">
        <Label>{t('Description')}</Label>
        <Textarea
          data-testid="landing-description-input"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('Landing page description')}
        />
      </div>
      <div className="space-y-2">
        <Label>{t('Tags')}</Label>
        <div className="flex gap-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            placeholder={t('Add tag')}
          />
          <Button type="button" variant="outline" onClick={addTag}>+</Button>
        </div>
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                {tag} x
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="landing-pages-skeleton">
        <XpnsrPageHeader kicker={t('Pages library')} title={t('Landing pages')} subtitle={t('Hosted pages and uploads')} />
        <XpnsrTableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="landing-pages-view">
      <BundleAudit kind="landing-pages" item={auditItem} onClose={() => setAuditItem(null)} />
      <XpnsrPageHeader
        kicker={t('Pages library')}
        title={
          <>
            {t('Landing pages')} <em>({pages.length})</em>
          </>
        }
        subtitle={t('Upload, organize and reuse landing pages across your campaigns.')}
        actions={
          <>
          <Button asChild variant="outline" data-testid="landing-pages-view-archived">
            <Link to="/archive">
              <Archive className="w-4 h-4 mr-2" />
              {t('View archived')}
            </Link>
          </Button>
          <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) resetUploadForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" onClick={resetUploadForm}>
                <Upload className="w-4 h-4 mr-2" />
                {t('Upload ZIP')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t('Upload Landing Page')}</DialogTitle>
                <DialogDescription>{t('Upload a ZIP file containing your landing page files (max 50MB)')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Name')} *</Label>
                    <Input
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('My landing page')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Category')}</Label>
                    <Select
                      value={uploadForm.category}
                      onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('Select category')} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('Description')}</Label>
                  <Textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('Landing page description')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Tags')}</Label>
                  <Input
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder={t('tag1, tag2, tag3')}
                  />
                  <p className="text-xs text-muted-foreground">{t('Separate tags with commas')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('ZIP File')} *</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <FileArchive className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-2">
                      {uploadForm.file ? uploadForm.file.name : t('Drag & drop or click to select')}
                    </p>
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      {t('Select ZIP File')}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">{t('Only ZIP files are supported')}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                  {t('Cancel')}
                </Button>
                <Button onClick={() => handleUpload()} disabled={uploading || !uploadForm.name || !uploadForm.file}>
                  {uploading ? t('Uploading...') : t('Upload')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-landing-btn" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Add landing page')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t('New landing page')}</DialogTitle>
                <DialogDescription>{t('Add a landing page for use in campaigns and A/B tests')}</DialogDescription>
              </DialogHeader>
              {PageFormFields()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('Cancel')}</Button>
                <Button data-testid="save-landing-btn" onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="landing-search-input"
          className="pl-10"
          placeholder={t('Search landing pages…')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

       {filtered.length === 0 ? (
         <Card>
           <CardContent style={{ padding: 0 }}>
             <XpnsrEmpty
               icon={FileText}
               title={searchQuery ? t('Nothing found') : t('No landing pages')}
               subtitle={t('Create landing pages for use in campaigns')}
               cta={!searchQuery ? (
                 <button
                   type="button"
                   className="xp-btn xp-btn-primary"
                   onClick={() => setCreateDialogOpen(true)}
                   data-testid="empty-create-landing"
                 >
                   <Plus style={{ width: 12, height: 12 }} /> {t('Create landing page')}
                 </button>
               ) : null}
             />
           </CardContent>
         </Card>
       ) : (
         <Card>
           <div className="overflow-x-auto">
             <Table>
               <TableHeader>
                 <TableRow>
                   <TableHead className="whitespace-nowrap">{t('Name')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('URL')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Category')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Visits')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Conversions')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('CR')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Status')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Actions')}</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {filtered.map((page) => (
                   <TableRow key={page.id} data-testid={`landing-row-${page.id}`}>
                     <TableCell className="whitespace-nowrap">
                       <div>
                         <div className="flex items-center gap-2">
                           <span className="font-medium">{page.name}</span>
                           {page.pages?.length > 1 && (
                             <Badge variant="secondary" className="text-xs">{page.pages.length} {t('pages')}</Badge>
                           )}
                         </div>
                         {page.tags?.length > 0 && (
                           <div className="flex gap-1 mt-1">
                             {page.tags.slice(0, 3).map(tag => (
                               <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                             ))}
                           </div>
                         )}
                       </div>
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <div className="flex items-center gap-1 max-w-[200px]">
                         <span className="truncate text-sm text-muted-foreground">{page.url}</span>
                         <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Copy landing page URL')} title={t('Copy landing page URL')} onClick={() => copyUrl(absUrl(page.url))}>
                           <Copy className="w-3 h-3" />
                         </Button>
                       </div>
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <Badge variant="outline">
                         {categories.find(c => c.value === page.category)?.label || page.category || '-'}
                       </Badge>
                     </TableCell>
                     <TableCell className="text-right font-medium whitespace-nowrap">{page.total_visits || 0}</TableCell>
                     <TableCell className="text-right font-medium whitespace-nowrap">{page.conversions || 0}</TableCell>
                     <TableCell className="text-right font-medium whitespace-nowrap">
                       {page.conversion_rate ? `${(Number(page.conversion_rate) || 0).toFixed(2)}%` : '0%'}
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <Badge className={page.is_active ? 'bg-green-100 text-green-800' : 'bg-surface-2 text-foreground'}>
                         {page.is_active ? t('Active') : t('Inactive')}
                       </Badge>
                     </TableCell>
                     <TableCell className="text-right whitespace-nowrap">
                       <div className="flex justify-end gap-1">
                         <Button variant="ghost" size="sm" onClick={() => setPreviewPage(page)} aria-label={t('Preview')} title={t('Preview')}>
                           <Eye className="w-4 h-4" />
                         </Button>
                         {page.is_uploaded && (
                           <Button variant="ghost" size="sm" onClick={() => openFileEditor(page)} aria-label={t('Edit files')} title={t('Edit files')}>
                             <FileCode className="w-4 h-4" />
                           </Button>
                         )}
                         {page.is_uploaded && (
                           <Button variant="ghost" size="sm" onClick={() => setAuditItem({ id: page.id, name: page.name })} aria-label={t('Check funnel')} title={t('Check funnel')}>
                             <ShieldCheck className="w-4 h-4" />
                           </Button>
                         )}
                         <Button variant="ghost" size="sm" aria-label={t('Open landing page')} title={t('Open landing page')} onClick={() => window.open(absUrl(page.url), '_blank', 'noopener,noreferrer')} data-testid={`landing-open-${page.id}`}>
                           <ExternalLink className="w-4 h-4" />
                         </Button>
                         <Button variant="ghost" size="sm" aria-label={t('Edit landing page')} title={t('Edit landing page')} onClick={() => openEdit(page)} data-testid={`landing-edit-${page.id}`}>
                           <Edit className="w-4 h-4" />
                         </Button>
                         {(page.is_uploaded || page.upload_path) && (
                           <Button
                             variant="ghost"
                             size="sm"
                             aria-label={t('Download ZIP')}
                             title={t('Download ZIP')}
                             onClick={() => downloadArchive(API, `/landing-pages/${page.id}/download`, page.name).catch(() => toast.error(t('Download failed')))}
                             data-testid={`download-${page.id}`}
                           >
                             <Download className="w-4 h-4" />
                           </Button>
                         )}
                         <Button variant="ghost" size="sm" aria-label={t('Delete landing page')} title={t('Delete landing page')} onClick={() => setDeleteTarget(page)} data-testid={`landing-delete-${page.id}`}>
                           <Trash2 className="w-4 h-4 text-red-500" />
                         </Button>
                       </div>
                     </TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </div>
         </Card>
       )}

      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingPage(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('Edit landing page')}</DialogTitle>
            <DialogDescription>{t('Edit landing page settings')}</DialogDescription>
          </DialogHeader>
          {PageFormFields()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t('Cancel')}</Button>
            <Button data-testid="update-landing-btn" onClick={handleUpdate}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Archive landing page')}
        description={t('This will move "{name}" to the archive. You can restore it or delete it permanently from the Archive page.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Archive')}
        variant="default"
        onConfirm={() => handleDelete(deleteTarget?.id)}
      />

      <Dialog open={!!previewPage} onOpenChange={(open) => { if (!open) setPreviewPage(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewPage?.name || t('Preview')}</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <span className="truncate">{previewPage?.url}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Open landing page')} title={t('Open landing page')} onClick={() => window.open(absUrl(previewPage?.url), '_blank', 'noopener,noreferrer')}>
                <ExternalLink className="w-3 h-3" />
              </Button>
            </DialogDescription>
          </DialogHeader>
          {previewPage?.url && (
            <div className="rounded-lg border overflow-hidden" style={{ height: '70vh' }}>
              <iframe
                src={absUrl(previewPage.url)}
                title={previewPage.name}
                className="w-full h-full"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Entry-point picker dialog */}
      <Dialog open={entryPickerOpen} onOpenChange={(open) => { if (!open) handleEntryPickerSkip(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Choose entry point')}</DialogTitle>
            <DialogDescription>{t('Multiple HTML pages detected. Select the main page visitors will land on.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {detectedPages.map((p) => (
              <label key={p} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedEntry === p ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
                <input type="radio" name="entry" value={p} checked={selectedEntry === p} onChange={() => setSelectedEntry(p)} className="accent-primary" />
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate">{p}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleEntryPickerSkip}>{t('Skip (use auto-detected)')}</Button>
            <Button onClick={handleEntryPointConfirm} disabled={uploading}>
              {uploading ? t('Uploading...') : t('Confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File editor dialog */}
      <Dialog open={fileEditorOpen} onOpenChange={(open) => { if (!open) { if (fileDirty && !window.confirm(t('Discard unsaved changes?'))) return; setFileEditorOpen(false); setFileEditorPage(null); setActiveFile(null); setFileContent(''); setFileDirty(false); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              {fileEditorPage?.name} — {t('File editor')}
            </DialogTitle>
            <DialogDescription>{t('Edit HTML, CSS, JS and other text files inside the uploaded archive')}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-4" style={{ height: '65vh' }}>
            {/* File tree sidebar */}
            <div className="w-64 flex-shrink-0 border rounded-lg overflow-y-auto bg-muted/30">
              <div className="p-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b flex items-center justify-between">
                <span>{t('Files')}</span>
                <label className="cursor-pointer text-primary hover:underline font-normal normal-case tracking-normal">
                  {t('+ Upload')}
                  <input type="file" className="hidden" onChange={(e) => { const fobj = e.target.files && e.target.files[0]; e.target.value = ""; editorUploadFile(fobj); }} />
                </label>
              </div>
              {fileLoading && !activeFile ? (
                <div className="p-4 text-sm text-muted-foreground">{t('Loading...')}</div>
              ) : (
                <div className="p-1">
                  {fileList.map((f) => (
                    <button
                      key={f.path}
                      onClick={() => f.editable && openFile(f.path)}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                        activeFile === f.path ? 'bg-primary/10 text-primary font-medium' : f.editable ? 'hover:bg-muted cursor-pointer' : 'text-muted-foreground cursor-default opacity-60'
                      }`}
                      title={f.editable ? f.path : t('Binary file — not editable')}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{f.path}</span>
                      {f.path === fileEditorPage?.index_file && (
                        <Badge variant="secondary" className="text-[10px] ml-auto flex-shrink-0">entry</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Editor area */}
            <div className="flex-1 flex flex-col min-w-0">
              {activeFile ? (
                <>
                  <div className="flex items-center justify-between pb-2 border-b mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                      <FileCode className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{activeFile}</span>
                      {fileDirty && <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">modified</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => editorDeleteFile(activeFile)} disabled={!activeFile || activeFile === fileEditorPage?.index_file} aria-label={activeFile === fileEditorPage?.index_file ? t('Set another entry page first') : t('Delete file')} title={activeFile === fileEditorPage?.index_file ? t('Set another entry page first') : t('Delete file')}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={saveFile} disabled={fileSaving || !fileDirty}>
                        <Save className="w-4 h-4 mr-1" />
                        {fileSaving ? t('Saving...') : t('Save')}
                      </Button>
                    </div>
                  </div>
                  <textarea
                    className="flex-1 w-full font-mono text-sm p-3 border rounded-lg resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    value={fileContent}
                    onChange={(e) => { setFileContent(e.target.value); setFileDirty(true); }}
                    spellCheck={false}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <FileCode className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{t('Select a file from the sidebar to edit')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
